import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Chip, Link as MuiLink, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ErrorBanner from '../../common/ErrorBanner';
import { getVoiceApps, updateVoiceApp } from '../../../api/backendApi';

const ActiveAppsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState({ sid: '', friendlyName: '', voiceUrl: '', statusCallbackUrl: '' });

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await getVoiceApps();
      const shaped = (Array.isArray(data) ? data : []).map((x, idx) => ({ id: x?.sid || x?.friendlyName || String(idx), ...x }));
      setRows(shaped);
    } catch (e) {
      setError(e?.message || 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let timer;
    load();
    timer = setInterval(load, 60000); // 60s
    return () => clearInterval(timer);
  }, [load]);

  const openEdit = useCallback((row) => {
    setEditing({
      sid: row?.sid || '',
      friendlyName: row?.friendlyName || '',
      voiceUrl: row?.voiceUrl || '',
      statusCallbackUrl: row?.statusCallbackUrl || '',
    });
    setEditOpen(true);
  }, []);

  const onChangeField = useCallback((e) => {
    const { name, value } = e.target;
    setEditing((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onSave = useCallback(async () => {
    try {
      setLoading(true);
      const { sid, friendlyName, voiceUrl, statusCallbackUrl } = editing;
      await updateVoiceApp(sid, { friendlyName, voiceUrl, statusCallbackUrl });
      await load();
      setEditOpen(false);
    } catch (e) {
      setError(e?.message || 'Failed to update app');
    } finally {
      setLoading(false);
    }
  }, [editing, load]);

  const columns = useMemo(() => ([
    { field: 'sid', headerName: 'App SID', flex: 1, minWidth: 180 },
    { field: 'friendlyName', headerName: 'Friendly Name', flex: 1, minWidth: 180 },
    { field: 'voiceUrl', headerName: 'Voice URL', flex: 1, minWidth: 220 },
    { field: 'statusCallbackUrl', headerName: 'Callback URL', flex: 1, minWidth: 220 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const ok = Boolean(params?.row?.voiceUrl) && Boolean(params?.row?.statusCallbackUrl);
        return (
          <Chip size="small" label={ok ? 'Active' : 'Error'} color={ok ? 'success' : 'error'} />
        );
      }
    },
    { field: 'dateCreated', headerName: 'Created', width: 180, valueGetter: (p) => (p?.row?.dateCreated ? String(p.row.dateCreated) : '') },
    { field: 'dateUpdated', headerName: 'Updated', width: 180, valueGetter: (p) => (p?.row?.dateUpdated ? String(p.row.dateUpdated) : '') },
    {
      field: 'console',
      headerName: 'Twilio Console',
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const sid = params?.row?.sid;
        if (!sid) return <span>-</span>;
        return (
          <MuiLink href={`https://console.twilio.com/us1/develop/voice/applications/${sid}`} target="_blank" rel="noopener">Open</MuiLink>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button size="small" variant="outlined" onClick={() => openEdit(params.row)}>Edit</Button>
      )
    }
  ]), [openEdit]);

  return (
    <Box>
      <ErrorBanner message={error} />
      <div style={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </div>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit TwiML Application</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="App SID" value={editing.sid} InputProps={{ readOnly: true }} fullWidth size="small" />
            <TextField label="Friendly Name" name="friendlyName" value={editing.friendlyName} onChange={onChangeField} fullWidth size="small" />
            <TextField label="Voice URL" name="voiceUrl" value={editing.voiceUrl} onChange={onChangeField} fullWidth size="small" />
            <TextField label="Status Callback URL" name="statusCallbackUrl" value={editing.statusCallbackUrl} onChange={onChangeField} fullWidth size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveAppsTab;
